import React, { useEffect, useRef } from "react";
import { Card, CardContent } from "@lemonadetraining/web-ui";
import { IconPlayerPause } from "@tabler/icons-react";
import {
  formatCommitmentDate,
  projectedPausedEndDate,
} from "@lemonadetraining/web-shared/utils/commitmentStatus";
import type { CardUpdateRedirect } from "../../hooks/useCardUpdateRedirect";
import CardUpdateButton from "./CardUpdateButton";
import {
  DUNNING_CONTACT,
  DUNNING_EXTENSION,
  DUNNING_EXTENSION_NO_DATE,
  DUNNING_PAUSED_BODY,
  DUNNING_PAUSED_TITLE,
} from "./dunningCopy";

interface DunningPausedCardProps {
  commitmentEndDate: Date;
  pausedAt: Date | null;
  cardUpdate: CardUpdateRedirect;
  timeZone?: string | null;
  /** The gym's zone is still loading: say it without the date for now. */
  datesPending?: boolean;
}

/**
 * Paused: the one designed state for a member locked out over a payment.
 * Focus lands on the heading so the change of state is announced without an
 * animation of our own, and the extension line states a concrete date.
 */
const DunningPausedCard: React.FC<DunningPausedCardProps> = ({
  commitmentEndDate,
  pausedAt,
  cardUpdate,
  timeZone,
  datesPending,
}) => {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const projectedEnd = pausedAt ? projectedPausedEndDate(commitmentEndDate, pausedAt) : null;

  return (
    <Card data-testid="dunning-paused" aria-live="polite">
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex items-center gap-2">
          <IconPlayerPause aria-hidden="true" className="size-5 text-foreground" />
          <h3
            ref={headingRef}
            tabIndex={-1}
            className="text-base font-semibold text-foreground outline-none"
            data-testid="dunning-paused-title"
          >
            {DUNNING_PAUSED_TITLE}
          </h3>
        </div>
        <p className="text-sm text-foreground">{DUNNING_PAUSED_BODY}</p>
        <CardUpdateButton
          cardUpdate={cardUpdate}
          className="w-full min-h-[44px] sm:w-auto"
          testId="dunning-paused-cta"
        />
        <Card className="bg-muted">
          <CardContent className="p-3">
            <p className="text-sm text-foreground" data-testid="dunning-extension">
              {projectedEnd && !datesPending
                ? DUNNING_EXTENSION(formatCommitmentDate(projectedEnd, timeZone))
                : DUNNING_EXTENSION_NO_DATE}
            </p>
          </CardContent>
        </Card>
        <p className="text-sm text-muted-foreground">{DUNNING_CONTACT}</p>
      </CardContent>
    </Card>
  );
};

export default DunningPausedCard;
